import { computed, effect, inject, Injectable, Signal, signal } from '@angular/core';
import { HttpClient, httpResource } from '@angular/common/http';
import { filter, map, tap } from 'rxjs';
import { toObservable } from '@angular/core/rxjs-interop';

interface AuthResponse {
  accessToken: string;
  role: 'guest' | 'admin';
}

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private readonly http = inject(HttpClient);
  private readonly tokenKey = 'auth_token';
  readonly token = signal<string | null>(this.getToken());

  constructor() {
    effect(() => {
      const tok = this.token();
      const user = this.theUser;
      if (tok && user.error()) {
        this.setToken(null);
      }
    });
  }

  isLoggedIn = computed(() => !!this.token() && !this.theUser.error() && !this.theUser.isLoading() && !!this.theUser.value());

  theUser = httpResource<{
    householdName: string;
    primaryName: string;
    email: string;
    phone: string;
    allowedCount: number;
    status: string;
    role: 'guest' | 'admin';
  } | null>(
    () => {
      const tok = this.token();
      if (!tok) {
        return undefined;
      }
      return { url: '/api/guests/me' };
    },
    { defaultValue: null },
  );

  role = computed(() => {
    const user = this.theUser;
    if (user.isLoading()) {
      return null;
    }
    if (user.error()) {
      return 'public';
    }
    const value = user.value();
    console.log('User value in role computation:', value);
    return value ? value.role : 'public';
  });

  role$ = toObservable(this.role).pipe(filter((r) => !!r));

  login(code: Signal<string | null>) {
    return httpResource<AuthResponse>(
      () => {
        const c = code();
        if (!c) {
          return undefined;
        }
        return { url: '/api/auth/login', method: 'POST', body: { code: c } };
      },
      {
        parse: (res) => {
          const resObj = res as AuthResponse;
          this.setToken(resObj.accessToken);
          return resObj;
        },
      },
    );
  }

  logout() {
    this.setToken(null);
  }

  private getToken(): string | null {
    if (typeof localStorage === 'undefined') {
      return null;
    }
    return localStorage.getItem(this.tokenKey);
  }

  private setToken(token: string | null): void {
    if (typeof localStorage === 'undefined') {
      return;
    }
    if (!token) {
      localStorage.removeItem(this.tokenKey);
      this.token.set(null);
    } else {
      localStorage.setItem(this.tokenKey, token);
      this.token.set(token);
    }
  }
}
